import { isMobile } from 'react-device-detect'

import { IProductCard } from '@src/components/product-card/product-card.types'
import { Filter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'

import { WidgetInfo } from '../../services/sandSearchService/searchService.types'
import MemoryBoundedLRUCache from '../../utils/memoryBoundedLRUCache'

interface SearchCacheItem {
  scrollY: number
  currentPage: number
  totalPages: number
  totalProducts: number
  products: IProductCard[]
  filters: Filter[]
  widgetInfo: WidgetInfo
}

const SEARCH_CACHE_EXPIRY = 5 * 60 * 1000 // 5 minutes
const LRU_CACHE_MAX_SIZE = isMobile ? 10 : 20 // 20MB
const LRU_CACHE_MAX_ITEMS = 3 // 3 items

// This is a singleton class that manages the search cache
class SearchCacheStateManager {
  private readonly lruCache: MemoryBoundedLRUCache
  private state: Record<string, SearchCacheItem> = {}

  constructor() {
    this.lruCache = new MemoryBoundedLRUCache(LRU_CACHE_MAX_ITEMS, LRU_CACHE_MAX_SIZE)
    this.state = {}
  }

  setSearchCache = (searchCache: SearchCacheItem, key: string) => {
    this.state[key] = searchCache
    this.lruCache.put(key, {
      searchCache: searchCache,
      cacheExpiry: Date.now() + SEARCH_CACHE_EXPIRY,
    })
  }

  getSearchCache = (query: string): SearchCacheItem | null => {
    const cacheData = this.lruCache.get(query)
    if (cacheData?.searchCache && cacheData.cacheExpiry > Date.now()) {
      return cacheData.searchCache
    }
    if (cacheData?.searchCache) {
      this.clearSearchCache(query)
    }
    return null
  }

  publishSearchCache = (query: string, scrollY: number) => {
    this.lruCache.put(query, {
      searchCache: { ...this.state[query], scrollY },
      cacheExpiry: Date.now() + SEARCH_CACHE_EXPIRY,
    })
  }

  clearSearchCache = (query: string) => {
    this.lruCache.delete(query)
  }

  deleteEntriesThatStartsWith = (query: string) => {
    const cache = (this.lruCache as any).cache as Map<string, any>
    for (const key of cache.keys()) {
      if (key.startsWith(query)) {
        this.lruCache.delete(key)
      }
    }
  }
}

const searchCacheStateManager = new SearchCacheStateManager()

export default searchCacheStateManager
